/**
 * VCI-P1 Slice 1 — Internal Venue Catalogue
 * Route: /venue-commerce/catalogue
 *
 * Internal UAT preview only. No public indexing.
 * Lists all suppliers tagged as venues (venue_type or venue_slug set).
 */
import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Hotel,
  MapPin,
  Search,
  Filter,
  Plus,
  ChevronRight,
  RefreshCw,
  CheckCircle,
  Clock,
  Eye,
  EyeOff,
  Shield,
  Building2,
  Loader2,
  SlidersHorizontal,
} from "lucide-react";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";

const API = process.env.REACT_APP_BACKEND_URL;

const VENUE_TYPES = [
  { value: "", label: "All Types" },
  { value: "FARMHOUSE", label: "Farmhouse" },
  { value: "RESORT", label: "Resort" },
  { value: "GARDEN", label: "Garden" },
  { value: "BANQUET_HALL", label: "Banquet Hall" },
  { value: "HOTEL", label: "Hotel" },
  { value: "HERITAGE", label: "Heritage" },
  { value: "OTHER", label: "Other" },
];

const VERIFICATION_STATUSES = [
  { value: "", label: "Any Status" },
  { value: "PENDING", label: "Pending" },
  { value: "VISITED", label: "Site Visited" },
  { value: "VERIFIED", label: "Verified" },
];

function VerificationBadge({ status }) {
  const cfg = {
    VERIFIED: { label: "Verified", icon: CheckCircle, cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
    VISITED:  { label: "Site Visited", icon: Eye, cls: "bg-blue-50 text-blue-700 border-blue-200" },
    PENDING:  { label: "Pending", icon: Clock, cls: "bg-amber-50 text-amber-700 border-amber-200" },
  }[status] || { label: status || "Pending", icon: Clock, cls: "bg-gray-50 text-gray-600 border-gray-200" };
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded border font-medium ${cfg.cls}`}>
      <Icon size={11} /> {cfg.label}
    </span>
  );
}

function PublicationBadge({ isPublished }) {
  return isPublished ? (
    <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded border bg-green-50 text-green-700 border-green-200 font-medium">
      <Eye size={11} /> Live
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded border bg-gray-50 text-gray-500 border-gray-200 font-medium">
      <EyeOff size={11} /> Draft
    </span>
  );
}

export default function VenueCatalogue() {
  const navigate = useNavigate();
  const [venues, setVenues] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [search, setSearch] = useState("");
  const [venueType, setVenueType] = useState("");
  const [verificationStatus, setVerificationStatus] = useState("");
  const [isPublished, setIsPublished] = useState("");
  const [showFilters, setShowFilters] = useState(false);

  const fetchVenues = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = { limit: 100 };
      if (search.trim()) params.search = search.trim();
      if (venueType) params.venue_type = venueType;
      if (verificationStatus) params.verification_status = verificationStatus;
      if (isPublished !== "") params.is_published = isPublished === "true";
      const res = await axios.get(`${API}/api/venue-commerce/venues`, { params, withCredentials: true });
      setVenues(res.data.results || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to load venues");
    } finally {
      setLoading(false);
    }
  }, [search, venueType, verificationStatus, isPublished]);

  useEffect(() => {
    const t = setTimeout(fetchVenues, 300);
    return () => clearTimeout(t);
  }, [fetchVenues]);

  const activeFilters = [venueType, verificationStatus, isPublished].filter(Boolean).length;

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-indigo-600 to-violet-600 flex items-center justify-center shadow-sm">
            <Hotel size={20} className="text-white" />
          </div>
          <div>
            <h1 className="text-xl font-semibold text-gray-900" data-testid="vci-catalogue-title">
              Venue Catalogue
            </h1>
            <p className="text-sm text-gray-500 flex items-center gap-1 mt-0.5">
              <Shield size={11} className="text-amber-500" />
              Internal UAT Preview — not publicly indexed
            </p>
          </div>
        </div>
        <Button
          size="sm"
          onClick={() => navigate("/suppliers/new")}
          data-testid="vci-add-venue-btn"
          className="gap-2"
        >
          <Plus size={14} /> Register Venue
        </Button>
      </div>

      {/* Search + Filters */}
      <div className="bg-white border border-gray-100 rounded-xl p-4 shadow-sm space-y-3">
        <div className="flex items-center gap-3">
          <div className="relative flex-1">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input
              placeholder="Search venues by name, city or slug..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="pl-9 h-9 text-sm"
              data-testid="vci-catalogue-search"
            />
          </div>
          <Button
            variant="outline"
            size="sm"
            className={`gap-2 h-9 ${showFilters ? "border-indigo-400 text-indigo-700 bg-indigo-50" : ""}`}
            onClick={() => setShowFilters(s => !s)}
            data-testid="vci-catalogue-filter-toggle"
          >
            <SlidersHorizontal size={14} />
            Filters
            {activeFilters > 0 && (
              <span className="w-4 h-4 rounded-full bg-indigo-600 text-white text-xs flex items-center justify-center">
                {activeFilters}
              </span>
            )}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="h-9 px-2"
            onClick={fetchVenues}
            data-testid="vci-catalogue-refresh"
          >
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
          </Button>
        </div>

        {showFilters && (
          <div className="grid grid-cols-3 gap-3 pt-1 border-t border-gray-50">
            <div className="space-y-1">
              <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Venue Type</label>
              <select
                value={venueType}
                onChange={e => setVenueType(e.target.value)}
                className="w-full h-8 border border-gray-200 rounded-lg px-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
                data-testid="vci-filter-venue-type"
              >
                {VENUE_TYPES.map(t => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Verification</label>
              <select
                value={verificationStatus}
                onChange={e => setVerificationStatus(e.target.value)}
                className="w-full h-8 border border-gray-200 rounded-lg px-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
                data-testid="vci-filter-verification"
              >
                {VERIFICATION_STATUSES.map(s => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </select>
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Publication</label>
              <select
                value={isPublished}
                onChange={e => setIsPublished(e.target.value)}
                className="w-full h-8 border border-gray-200 rounded-lg px-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
                data-testid="vci-filter-publication"
              >
                <option value="">All</option>
                <option value="true">Live</option>
                <option value="false">Draft</option>
              </select>
            </div>
          </div>
        )}
      </div>

      {/* Stats bar */}
      <div className="flex items-center justify-between text-sm text-gray-500">
        <span data-testid="vci-catalogue-count">
          {loading ? "Loading…" : `${total} venue${total !== 1 ? "s" : ""}`}
        </span>
        {activeFilters > 0 && (
          <button
            className="text-indigo-600 hover:text-indigo-800 text-xs font-medium"
            onClick={() => { setVenueType(""); setVerificationStatus(""); setIsPublished(""); }}
            data-testid="vci-clear-filters"
          >
            Clear filters
          </button>
        )}
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm" data-testid="vci-catalogue-error">
          {error}
        </div>
      )}

      {/* Venue grid */}
      {loading && venues.length === 0 ? (
        <div className="flex items-center justify-center h-40 text-gray-400 gap-2">
          <Loader2 size={18} className="animate-spin" />
          <span className="text-sm">Loading venues…</span>
        </div>
      ) : venues.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-48 text-gray-400 gap-3">
          <Hotel size={36} className="opacity-30" />
          <p className="text-sm font-medium">No venues found</p>
          <p className="text-xs text-gray-400">Register a supplier as a venue to see it here</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="vci-venue-grid">
          {venues.map(venue => (
            <VenueCard key={venue.id} venue={venue} onClick={() => navigate(`/venue-commerce/${venue.id}`)} />
          ))}
        </div>
      )}
    </div>
  );
}

function VenueCard({ venue, onClick }) {
  const city = venue.geo_location?.city || venue.city || "—";
  const slug = venue.venue_slug;
  const venueType = venue.venue_type
    ? venue.venue_type.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())
    : null;
  const spacesCount = (venue.venue_spaces || []).length;
  const agreementsCount = (venue.commercial_agreements || []).filter(a => a.is_active).length;
  const verStatus = venue.verification?.status || "PENDING";

  return (
    <div
      className="bg-white border border-gray-100 rounded-xl p-4 shadow-sm hover:shadow-md hover:border-indigo-200 transition-all cursor-pointer group"
      onClick={onClick}
      data-testid={`vci-venue-card-${venue.id}`}
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-lg bg-indigo-50 flex items-center justify-center flex-shrink-0">
            <Building2 size={16} className="text-indigo-600" />
          </div>
          <div className="min-w-0">
            <h3 className="font-semibold text-sm text-gray-900 truncate group-hover:text-indigo-700 transition-colors" data-testid={`vci-venue-name-${venue.id}`}>
              {venue.display_name || venue.company_name}
            </h3>
            {slug && (
              <p className="text-xs text-gray-400 font-mono truncate">{slug}</p>
            )}
          </div>
        </div>
        <ChevronRight size={14} className="text-gray-300 group-hover:text-indigo-400 transition-colors flex-shrink-0 mt-1" />
      </div>

      <div className="flex flex-wrap items-center gap-1.5 mb-3">
        {venueType && (
          <Badge variant="outline" className="text-xs px-2 py-0 border-indigo-200 text-indigo-700 bg-indigo-50">
            {venueType}
          </Badge>
        )}
        <VerificationBadge status={verStatus} />
        <PublicationBadge isPublished={venue.is_published} />
      </div>

      <div className="flex items-center gap-3 text-xs text-gray-500">
        <span className="flex items-center gap-1">
          <MapPin size={11} className="text-gray-400" />
          {city}
        </span>
        {spacesCount > 0 && (
          <span className="flex items-center gap-1">
            <Building2 size={11} className="text-gray-400" />
            {spacesCount} space{spacesCount !== 1 ? "s" : ""}
          </span>
        )}
        {agreementsCount > 0 && (
          <span className="flex items-center gap-1">
            <Shield size={11} className="text-indigo-400" />
            {agreementsCount} agr.
          </span>
        )}
      </div>

      {venue.pricing_indicative_from != null && (
        <div className="mt-2 pt-2 border-t border-gray-50 text-xs text-gray-500">
          From{" "}
          <span className="font-semibold text-gray-700">
            {venue.pricing_currency || "INR"} {Number(venue.pricing_indicative_from).toLocaleString("en-IN")}
          </span>
        </div>
      )}
    </div>
  );
}
